import React, { useEffect, useState } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  FormHelperText,
} from '@material-ui/core';
import axios from 'axios';
import { useApi, identityApiRef, discoveryApiRef } from '@backstage/core-plugin-api';

type ProjectDetail = {
  name: string;
  adoProject: string;
  adoOrg?: string;
};

/**
 * ADO project names are stored URL-encoded (e.g. `MMIT%20Commercial%20Platform`) because
 * they feed ADO URLs and pipeline checkouts. Decode for display only — the selected value
 * stays encoded so downstream URLs keep working.
 */
const decodeAdoProject = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

/**
 * Selects an Azure DevOps project (e.g. "RAP").
 *
 * Distinct from SelectAdoProjects (plural), which despite its title returns Backstage
 * `project_name` values. One ADO project contains many project_names, so this field is
 * meant to be paired with SelectProjectName to narrow down to a single Backstage project.
 */
export const SelectAdoProject = (
  props: FieldExtensionComponentProps<string>,
) => {
  const { onChange, formData, required, rawErrors, schema } = props;
  const [options, setOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const identityApi = useApi(identityApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  useEffect(() => {
    async function fetchAdoProjects() {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
        const { token } = await identityApi.getCredentials();
        const profile = await identityApi.getProfileInfo();

        const res = await axios.post(
          `${baseUrl}/ado/user/projects`,
          {
            username: profile.email,
            forceRefresh: false,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          },
        );

        const details = res.data.projectDetails as ProjectDetail[] | undefined;
        if (Array.isArray(details)) {
          // Collapse many project_names down to the distinct ADO projects they live under.
          const distinct = Array.from(
            new Set(details.map(d => d.adoProject).filter(Boolean)),
          ).sort((a, b) => decodeAdoProject(a).localeCompare(decodeAdoProject(b)));
          setOptions(distinct);
        } else {
          console.warn('SelectAdoProject: no projectDetails in /ado/user/projects response');
          setFetchError('Could not load ADO projects. Please try again.');
        }
      } catch (e) {
        console.error('Error fetching ADO projects:', e);
        setFetchError('Failed to fetch ADO projects. Please try again.');
      } finally {
        setLoading(false);
      }
    }

    fetchAdoProjects();
  }, [identityApi, discoveryApi]);

  const labelId = 'select-ado-project-label';

  return (
    <FormControl
      fullWidth
      required={required}
      error={rawErrors?.length > 0 || !!fetchError}
      variant="outlined"
    >
      <InputLabel id={labelId}>{schema.title}</InputLabel>
      {loading ? (
        <CircularProgress size={24} />
      ) : (
        <>
          <Select
            labelId={labelId}
            value={formData || ''}
            onChange={e => onChange(e.target.value as string)}
            label={schema.title}
          >
            {options.map(opt => (
              <MenuItem key={opt} value={opt}>
                {decodeAdoProject(opt)}
              </MenuItem>
            ))}
          </Select>
          {schema.description && !fetchError && (
            <FormHelperText>{schema.description}</FormHelperText>
          )}
          {fetchError && <FormHelperText>{fetchError}</FormHelperText>}
          {rawErrors && rawErrors.length > 0 && (
            <FormHelperText>{rawErrors.join(', ')}</FormHelperText>
          )}
        </>
      )}
    </FormControl>
  );
};
